/**
 * Read-only preview of a custom page. Two modes:
 *  - `url`     → live render of the source page in a sandboxed iframe (used by the
 *                create/edit form, where the stored content doesn't exist yet).
 *  - `content` → base64 of the platform-rendered HTML (used by the list), decoded
 *                and shown via `srcDoc`.
 * `url` takes precedence when both are supplied.
 */
import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
} from '@vnetwork/ui/primitives';

/** Decode a base64 string to HTML; returns null on empty/invalid input. */
function decodeContent(content?: string): string | null {
  if (!content) return null;
  try {
    return atob(content);
  } catch {
    return null;
  }
}

export function CustomPagePreviewDialog({
  name,
  content,
  url,
  open,
  onOpenChange,
}: {
  name: string;
  content?: string;
  url?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const html = useMemo(() => decodeContent(content), [content]);
  const liveUrl = url?.trim();

  const frameStyle = {
    width: '100%',
    height: 420,
    border: '1px solid var(--vn-border-default)',
    borderRadius: 'var(--vn-radius-md)',
    background: 'var(--vn-bg-surface)',
  } as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Preview — {name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {liveUrl ? (
            <iframe
              title={`Preview of ${name}`}
              src={liveUrl}
              sandbox="allow-scripts allow-same-origin"
              style={frameStyle}
            />
          ) : html ? (
            <iframe title={`Preview of ${name}`} srcDoc={html} sandbox="" style={frameStyle} />
          ) : (
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              No preview available.
            </span>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CustomPagePreviewDialog;
